import React from 'react';
import { Link } from 'react-router-dom';
import {
  List as MuiList,
  type ListProps,
  ListItem as MuiListItem,
  ListItemAvatar as MuiListItemAvatar,
  type ListItemProps,
  ListItemSecondaryAction as MuiListItemSecondaryAction,
  ListItemText as MuiListItemText,
  type ListItemTextProps as MuiListItemTextProps,
} from '@material-ui/core';
import { Button } from '@popmenu/admin-ui';
import { Avatar } from '@popmenu/common-ui';

import { type WithStyles } from '~/utils/withStyles';
import { withStyles } from '../../utils/withStyles';
import basicListStyles from '../../assets/jss/shared/basicListStyles';

const BasicListItemAction = (props: React.ComponentProps<typeof Button>) => (
  <MuiListItemSecondaryAction>
    <Button {...props} />
  </MuiListItemSecondaryAction>
);

const BasicListItemAvatar = (props: React.ComponentProps<typeof Avatar>) => (
  <MuiListItemAvatar>
    <Avatar {...props} />
  </MuiListItemAvatar>
);

const BasicListItemText = ({ primary = null, secondary = null, ...props }: MuiListItemTextProps) => (
  <MuiListItemText primary={primary} secondary={secondary} {...props} />
);

interface BasicListItemBaseProps {
  avatarUrl?: string | null;
  children?: React.ReactNode;
  primaryText?: React.ReactNode;
  secondaryText?: React.ReactNode;
}

type BasicListItemProps<C extends React.ElementType> = BasicListItemBaseProps & WithStyles<typeof basicListStyles> &
  Omit<ListItemProps<C, { component?: C }>, 'classes'>;

const BasicListItem = <C extends React.ElementType = 'div'>({
  avatarUrl = null,
  children,
  classes,
  // @ts-expect-error The default is correct
  component = 'div',
  primaryText = null,
  secondaryText = null,
  ...props
}: BasicListItemProps<C>) => (
  <MuiListItem className={(classes as Record<'listItem', string>).listItem} component={component} disableGutters {...props}>
    <React.Fragment>
      {avatarUrl && (
        <BasicListItemAvatar src={avatarUrl} size="large" />
      )}
      {(primaryText || secondaryText) && (
        <BasicListItemText primary={primaryText} secondary={secondaryText} />
      )}
      {children}
    </React.Fragment>
  </MuiListItem>
  );

type BasicListItemLinkProps =
  | ({ to: string, href?: never } & BasicListItemProps<typeof Link>)
  | ({ to?: never, href?: string } & BasicListItemProps<'a'>);

const BasicListItemLink = ({ href, to, ...props }: BasicListItemLinkProps) => (
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  <BasicList.Item button component={to ? Link : 'a'} href={href} to={to} {...props} />
);

type BasicListProps = Omit<ListProps<'div'>, 'component'>;

const BasicList = (props: BasicListProps) => (
  <MuiList component="div" {...props} />
);

BasicList.Item = withStyles(basicListStyles)(BasicListItem);
BasicList.ItemAction = BasicListItemAction;
BasicList.ItemLink = BasicListItemLink;
BasicList.ItemAvatar = BasicListItemAvatar;
BasicList.ItemText = BasicListItemText;

export default BasicList;
